import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { applicationsClient } from '@terra/shared-rest-clients-applications';
import { getCurrentGatewayUrl } from '../../utils/environmentDetection';
import { getAuthHeaders } from '../../utils/authHeaders';
import { getDecryptedCredentialsQueryKey } from '../../components/settings/target-settings/applicationCredential.transforms';

const fetchApplicationCredentials = async ({
  applicationId,
  decrypt,
}: {
  applicationId: string;
  decrypt: boolean;
}) => {
  const authHeaders = await getAuthHeaders();
  const response = await applicationsClient.getCredentials({
    params: { id: applicationId },
    query: { decrypt, includeDisabled: true },
    overrideClientOptions: {
      baseUrl: `${getCurrentGatewayUrl()}/orchestrator`,
    },
    extraHeaders: {
      ...authHeaders,
    },
  });

  if (response.status === 200) {
    return response.body;
  }

  console.warn('Failed to fetch application credentials', {
    applicationId,
    decrypt,
    status: response.status,
  });

  if (decrypt && response.status === 403) {
    console.warn(
      'Decrypt credentials denied; loading redacted credentials list',
      { applicationId, status: response.status },
    );

    return fetchApplicationCredentials({
      applicationId,
      decrypt: false,
    });
  }

  throw new Error(`Failed to fetch credentials (${String(response.status)})`);
};

export function useDecryptedApplicationCredentials({
  applicationId,
  tenantId,
}: {
  applicationId?: string;
  tenantId?: string;
}) {
  return useQuery({
    queryKey: getDecryptedCredentialsQueryKey(applicationId ?? ''),
    queryFn: async () => {
      if (!applicationId || !tenantId) {
        return { credentials: [] };
      }

      return fetchApplicationCredentials({
        applicationId,
        decrypt: true,
      });
    },
    enabled: !!applicationId && !!tenantId,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });
}

export function useInvalidateApplicationCredentials() {
  const queryClient = useQueryClient();

  return useCallback(
    async (applicationId: string) => {
      await queryClient.invalidateQueries({
        queryKey: ['application-credentials', applicationId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['cached-credentials', applicationId],
      });
      await queryClient.refetchQueries({
        queryKey: getDecryptedCredentialsQueryKey(applicationId),
      });
    },
    [queryClient],
  );
}
